import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react'
import {
  Download,
  Copy,
  FileText,
  Link as LinkIcon,
  Clipboard,
  Share,
  ExternalLink,
  Settings as SettingsIcon,
} from 'lucide-react'
import React, { useState, useEffect, memo, useRef, useMemo } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

// --- Data Settings imports ---
import useDataSettings from '@/hooks/useDataSettings'
import { AICommandType, createAICommand, useAICommandState } from '@/utils/aiCommandUtils'
import { copyToClipboard as copyMarkdownToClipboard } from '@/utils/documentUtils'
import styles from '~/styles/NodeResource.module.css'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

interface NodeResourceData {
  fileId: string
  filename: string
  url: string
  contentType: string
  size: number
  createdAt: string
  groupId: string | null
  groupName?: string
  blobName?: string
  assignedEmails?: string[] | string
  // Add dataSettings for type safety
  dataSettings?: any
  // URL resource type support
  isUrlResource?: boolean
  urlToDownload?: string
  downloadStatus?: 'pending' | 'downloading' | 'completed' | 'failed'
  downloadAttempted?: boolean
  // Google Drive URL support
  googleDriveUrl?: string
  isGoogleDriveFile?: boolean
  googleDriveProcessing?: boolean
}

const NodeResource: React.FC<NodeProps & { data: NodeResourceData }> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()

  // Track if label has been initialized to prevent infinite loops
  const [labelInitialized, setLabelInitialized] = useState(false)

  // Initialize label from filename in useEffect instead of during render
  useEffect(() => {
    if (
      !labelInitialized &&
      (data.label === undefined || data.label === null || data.label === '' || data.label === 'File Resource') &&
      data.filename &&
      data.filename !== 'File Resource'
    ) {
      setLabelInitialized(true)
      updateNodeData(id, { ...data, label: data.filename })
    }
  }, [id, data, updateNodeData, labelInitialized])

  const [isLocked, setIsLocked] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>('normal')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(data.filename || 'File Resource')
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  )
  const [copyFeedback, setCopyFeedback] = useState<string>('')
  
  // --- Google Drive URL state ---
  const [googleDriveUrl, setGoogleDriveUrl] = useState<string>(data.googleDriveUrl || '')
  const [isProcessingGoogleDrive, setIsProcessingGoogleDrive] = useState<boolean>(data.googleDriveProcessing || false)

  // --- Data Settings state ---
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  // Ref for markdown preview (for HTML copy)
  const markdownPreviewRef = useRef<HTMLDivElement>(null)

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = {
    url: {
      value: data.url,
      enabled: true,
      label: 'File URL',
      description: 'Direct URL to the file resource',
      permissions: { readable: true, writable: false },
    },
    fileAsMarkdown: {
      value: data.fileAsMarkdown || '',
      enabled: !!data.fileAsMarkdown,
      label: 'File as Markdown',
      description: 'File content converted to Markdown (if available)',
      permissions: { readable: true, writable: false },
    },
    filename: {
      value: data.filename,
      enabled: true,
      label: 'Filename',
      description: 'Name of the file',
      permissions: { readable: true, writable: false },
    },
    contentType: {
      value: data.contentType,
      enabled: true,
      label: 'Content Type',
      description: 'MIME type of the file',
      permissions: { readable: true, writable: false },
    },
    size: {
      value: data.size,
      enabled: true,
      label: 'File Size',
      description: 'Size of the file in bytes',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => updateNodeData(id, { ...data, ...newData }),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [], // always empty
      exposedData: outgoingFields,
      availableNodes: [], // not relevant
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      updateNodeData(id, { ...data, dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.url,
    data.fileAsMarkdown,
    data.filename,
    data.contentType,
    data.size,
  ])

  // Check if file is an image
  const isImage = data.contentType?.startsWith('image/')

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Copy to clipboard
  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(message)
      },
      () => {
        toast.error('Failed to copy to clipboard')
      }
    )
  }

  // Copy image to clipboard
  const copyImageToClipboard = async () => {
    try {
      const response = await fetch(data.url)
      const blob = await response.blob()
      
      // Create a ClipboardItem
      const item = new ClipboardItem({ [blob.type]: blob })
      
      // Write to clipboard
      await navigator.clipboard.write([item])
      toast.success('Image copied to clipboard')
    } catch (error) {
      console.error('Error copying image to clipboard:', error)
      toast.error('Failed to copy image to clipboard')
    }
  }

  // Copy markdown link
  const copyMarkdownLink = () => {
    const markdownLink = isImage 
      ? `![${data.filename}](${data.url})`
      : `[${data.filename}](${data.url})`
    copyToClipboard(markdownLink, 'Markdown link copied to clipboard')
  }

  // Download file
  const downloadFile = () => {
    const link = document.createElement('a')
    link.href = data.url
    link.setAttribute('download', data.filename || 'download')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Initialize minimize level from data
  useEffect(() => {
    setMinimizeLevel(data.minimizeLevel || 'normal')
    setIsMinimized((data.minimizeLevel || 'normal') !== 'normal')
  }, [data.minimizeLevel])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (data.filename) {
      minifiedInfo.filename = data.filename;
    }
    
    if (data.contentType) {
      minifiedInfo['content type'] = data.contentType;
    }
    
    if (data.size) {
      minifiedInfo.size = formatFileSize(data.size);
    }
    
    if (data.fileAsMarkdown?.trim()) {
      minifiedInfo['has content'] = 'Yes';
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo });
    }
  }, [data.filename, data.contentType, data.size, data.fileAsMarkdown, id]); // ✅ REMOVED: data.minifiedInfo, updateNodeData from deps

  // Node action handlers
  const handleLock = () => {
    const newLockState = !isLocked
    setIsLocked(newLockState)
    updateNodeData(id, { ...data, isLocked: newLockState })
  }

  const handleMinimize = () => {
    const newMinState = !isMinimized
    setIsMinimized(newMinState)
    setMinimizeLevel(newMinState ? 'medium' : 'normal')
    updateNodeData(id, { 
      ...data, 
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal'
    })
  }

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, { 
      ...data, 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  const handleTitleChange = (newTitle: string) => {
    updateNodeData(id, { 
      ...data, 
      label: newTitle, 
      filename: newTitle // keep filename in sync with label
    })
  }

  const handleTitleSave = () => {
    handleTitleChange(editingTitleValue)
    setIsEditingTitle(false)
  }

  // --- AI Command State for NodeResource ---
  const command = data.documentRequest
  const { isProcessing, statusMessage } = useAICommandState(command, AICommandType.NODE_RESOURCE)

  // --- Manual trigger for Extract MD ---
  const handleExtractMarkdown = () => {
    if (data.url) {
      updateNodeData(id, {
        ...data,
        documentRequest: createAICommand(AICommandType.NODE_RESOURCE, { extractionType: 'markdown' })
      })
    } else {
      toast.error('No file URL available to extract Markdown');
    }
  }

  // --- Manual trigger for PDF-to-MD conversion ---
  const handleConvertPdfToMd = () => {
    if (data.url) {
      updateNodeData(id, {
        ...data,
        documentRequest: createAICommand(AICommandType.NODE_RESOURCE, { extractionType: 'pdf-to-md' })
      })
    } else {
      toast.error('No file URL available for PDF conversion');
    }
  }

  // Copy Markdown to clipboard (like NodeDocument)
  const handleCopyToClipboard = () => {
    if (data.fileAsMarkdown) {
      copyMarkdownToClipboard(data.fileAsMarkdown, markdownPreviewRef, setCopyFeedback)
    }
  }

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (isProcessing) return "loading";
    if (statusMessage && statusMessage.toLowerCase().includes("error")) return "error";
    if (data.fileAsMarkdown) return "success";
    return "initial";
  }, [isProcessing, statusMessage, data.fileAsMarkdown]);

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      minWidth={400}
      minHeight={300}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      nodeTitle={data.label || data.filename || 'File Resource'}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimize}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={handleTitleChange}
      assignedEmails={Array.isArray(assignedEmails) ? assignedEmails : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          setAssignedEmails(emailsArr)
          updateNodeData(id, { ...data, assignedEmails: emailsArr })
        }
      }}
      onDataSettingsClick={() => setIsDataSettingsDialogVisible(true)}
      status={nodeStatus}
      selected={selected} // ✅ CRITICAL: Add the missing selected prop!
    >
      <div className={styles.nodeResource} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Remove ScrollArea, use plain div */}
        <div className={styles.scrollArea} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className={styles.content} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {minimizeLevel === 'normal' && (
              <>
                {/* Preview Container */}
                <div className={styles.previewContainer}>
                  {isImage ? (
                    <div className={styles.imagePreview}>
                      <img
                        src={data.url}
                        alt={data.filename}
                        className={styles.previewImage}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          const parent = e.currentTarget.parentElement
                          if (parent) {
                            const placeholder = document.createElement('div')
                            placeholder.innerHTML = '<FileText className="h-16 w-16 text-gray-400" />'
                            parent.appendChild(placeholder)
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <FileText className={styles.placeholderIcon} />
                  )}
                </div>

                {/* Metadata */}
                <div className={styles.metaData}>
                  <span className={styles.metaLabel}>File Type:</span>
                  <span className={styles.metaValue}>{data.contentType || 'Unknown'}</span>

                  <span className={styles.metaLabel}>Size:</span>
                  <span className={styles.metaValue}>{formatFileSize(data.size)}</span>

                  <span className={styles.metaLabel}>Created:</span>
                  <span className={styles.metaValue}>{formatDate(data.createdAt)}</span>

                  {data.groupName && (
                    <>
                      <span className={styles.metaLabel}>Group:</span>
                      <span className={styles.metaValue}>{data.groupName}</span>
                    </>
                  )}
                </div>

                {/* Markdown Preview */}
                <div style={{ marginTop: 16, position: 'relative', flex: 1, display: 'flex', minHeight: 0, maxHeight: '40%' }}>
                  {isProcessing && (
                    <div style={{ color: '#888', fontStyle: 'italic' }}>
                      {statusMessage || 'Converting file to Markdown...'}
                    </div>
                  )}
                  {!isProcessing && data.fileAsMarkdown && (
                    <div style={{ position: 'relative', flex: 1, display: 'flex', minHeight: 0 }}>
                      {/* Copy button in top right corner */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyToClipboard}
                        disabled={!data.fileAsMarkdown}
                        style={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          zIndex: 2,
                          padding: 4,
                          minWidth: 64,
                          minHeight: 64,
                          height: 32,
                          width: 32,
                          marginRight: 16,
                        }}
                        title="Copy to clipboard"
                      >
                        {copyFeedback ? copyFeedback : <Copy className="h-8 w-8" />}
                      </Button>
                      <div
                        ref={markdownPreviewRef}
                        style={{
                          background: '#f8f8f8',
                          border: '1px solid #eee',
                          borderRadius: 4,
                          padding: 12,
                          marginTop: 8,
                          flex: 1,
                          minHeight: 0,
                          height: '100%',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          fontFamily: 'monospace',
                          fontSize: 14,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          position: 'relative'
                        }}
                      >
                        {data.fileAsMarkdown}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions - always at bottom */}
        {minimizeLevel === 'normal' && (
          <div
            className={styles.actionButtons}
            style={{
              marginTop: 'auto',
              padding: '0.75rem 1rem 0.5rem 1rem',
              background: 'var(--background, #fff)',
              borderTop: '1px solid #eee',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadFile}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(data.url, 'URL copied to clipboard')}
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Copy URL
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={copyMarkdownLink}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                Copy Embeddable Link
              </Button>

              {isImage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyImageToClipboard}
                >
                  <Copy className="mr-8 h-8 w-8" />
                  Copy Image
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(data.url, '_blank')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDataSettingsDialogVisible(true)}
                title="Data Settings"
              >
                <SettingsIcon className="mr-2 h-4 w-4" />
                Data Settings
              </Button>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Button
                variant="default"
                size="sm"
                onClick={handleExtractMarkdown}
                disabled={isProcessing || !data.url}
              >
                Extract Data as Text
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleConvertPdfToMd}
                disabled={isProcessing || !data.url}
                style={{ marginLeft: '0.5rem' }}
              >
                PDF to Markdown
              </Button>
            </div>
          </div>
        )}

        {/* Output Handle */}
        <div className={styles.handleWrapper}>
          <Handle 
            type="source" 
            position={Position.Bottom} 
            id="data-output" 
            style={{ 
              background: '#6366F1',
              width: '12px',
              height: '12px'
            }} 
          />
        </div>
      </div>
      {/* Data Settings Dialog */}
      <NodeDataSettingsDialog
        visible={isDataSettingsDialogVisible}
        onHide={() => setIsDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={() => {}} // Not used for Resource node
        onSyncInputLink={() => {}} // Not used for Resource node
        forceShowAvailableNodes={false}
        debug={false}
      />
    </NodeBaseContainer>
  )
}

export default memo(NodeResource)